import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';
import CopyButton from '../common/CopyButton';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { shortenAddress } from '../../utils/wallet';

// Owner-only controls to change the `admin` address on Logic + Storage.
// Both contracts have separate onlyOwner updateAdmin(address) — this UI lets you update either
// one individually or both in sequence with a single click.
const OwnerControls = () => {
  const { logic, storage } = useContracts();
  const notify = useNotify();

  const [state, setState] = useState({
    logicAdmin: null,
    storageAdmin: null,
    logicOwner: null,
    storageOwner: null
  });
  const [loading, setLoading] = useState(true);
  const [newAdmin, setNewAdmin] = useState('');
  const [busy, setBusy] = useState(null);
  const runIdRef = useRef(0);

  const load = async () => {
    const myRun = ++runIdRef.current;
    setLoading(true);
    try {
      const [lAdmin, sAdmin, lOwner, sOwner] = await Promise.all([
        logic.admin().catch(() => null),
        storage.admin().catch(() => null),
        logic.owner().catch(() => null),
        storage.owner().catch(() => null)
      ]);
      if (myRun !== runIdRef.current) return;
      setState({ logicAdmin: lAdmin, storageAdmin: sAdmin, logicOwner: lOwner, storageOwner: sOwner });
    } catch (_) { /* ignore */ }
    finally {
      if (myRun === runIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const eq = (a, b) => a && b && a.toLowerCase() === b.toLowerCase();
  const adminsMatch = eq(state.logicAdmin, state.storageAdmin);

  const runTx = async (label, txPromise) => {
    const l = notify.loading(`${label}...`);
    try {
      const tx = await txPromise;
      notify.dismiss(l);
      const m = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(m);
      notify.success(`${label} — done`);
      return true;
    } catch (err) {
      notify.dismiss(l);
      notify.error(parseError(err));
      return false;
    }
  };

  const updateLogicAdmin = async () => {
    if (!ethers.utils.isAddress(newAdmin)) { notify.error('Invalid address'); return; }
    setBusy('logic');
    await runTx('Updating Logic admin', logic.updateAdmin(newAdmin));
    await load();
    setBusy(null);
  };

  const updateStorageAdmin = async () => {
    if (!ethers.utils.isAddress(newAdmin)) { notify.error('Invalid address'); return; }
    setBusy('storage');
    await runTx('Updating Storage admin', storage.updateAdmin(newAdmin));
    await load();
    setBusy(null);
  };

  const updateBoth = async () => {
    if (!ethers.utils.isAddress(newAdmin)) { notify.error('Invalid address'); return; }
    setBusy('both');
    const ok1 = await runTx('Updating Storage admin', storage.updateAdmin(newAdmin));
    if (ok1) {
      await runTx('Updating Logic admin', logic.updateAdmin(newAdmin));
    }
    await load();
    setBusy(null);
    setNewAdmin('');
  };

  return (
    <Card title="Change Admin Address" subtitle="Update the operator address on either or both contracts" glow>
      {loading ? (
        <div className="text-center" style={{ padding: 24 }}><MiniLoader /></div>
      ) : (
        <>
          {/* Current admins side-by-side */}
          <div className="admin-pair-grid">
            <div className="admin-pair-cell">
              <div className="admin-pair-label">Storage Admin</div>
              <div className="admin-pair-value font-mono">
                {state.storageAdmin ? shortenAddress(state.storageAdmin, 6) : '-'}
              </div>
              {state.storageAdmin && <CopyButton text={state.storageAdmin} label="Copy" />}
            </div>
            <div className="admin-pair-cell">
              <div className="admin-pair-label">Logic Admin</div>
              <div className="admin-pair-value font-mono">
                {state.logicAdmin ? shortenAddress(state.logicAdmin, 6) : '-'}
              </div>
              {state.logicAdmin && <CopyButton text={state.logicAdmin} label="Copy" />}
            </div>
          </div>

          <div className="mt-md mb-md">
            {adminsMatch ? (
              <span className="badge badge-success">✓ Storage & Logic admin match</span>
            ) : (
              <span className="badge badge-warning">⚠ Storage & Logic admin do NOT match — update both</span>
            )}
          </div>

          {/* New admin input */}
          <div className="input-group">
            <label className="input-label">New Admin Address</label>
            <input
              className="input font-mono"
              placeholder="0x..."
              value={newAdmin}
              onChange={(e) => setNewAdmin(e.target.value)}
            />
            <span className="input-hint">Enter the new admin address to apply on either or both contracts.</span>
          </div>

          <div className="grid grid-3 mt-md">
            <button
              className="btn btn-secondary btn-sm"
              onClick={updateStorageAdmin}
              disabled={busy !== null || !newAdmin}
            >
              {busy === 'storage' ? <><MiniLoader /> Storage</> : 'Storage Only'}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={updateLogicAdmin}
              disabled={busy !== null || !newAdmin}
            >
              {busy === 'logic' ? <><MiniLoader /> Logic</> : 'Logic Only'}
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={updateBoth}
              disabled={busy !== null || !newAdmin}
            >
              {busy === 'both' ? <><MiniLoader /> Both</> : 'Update Both'}
            </button>
          </div>

          <div className="text-muted mt-md" style={{ fontSize: 12, padding: '10px 12px', background: 'rgba(5,6,26,0.4)', borderRadius: 10 }}>
            💡 <strong>Update Both</strong> runs the Storage tx first, then the Logic tx. If the Storage tx
            fails or is rejected, the Logic tx is skipped so the two stay in sync only if the first succeeds.
            Requires 2 wallet signatures.
          </div>
        </>
      )}
    </Card>
  );
};

export default OwnerControls;
